import React, { useMemo } from "react";
import { motion } from "framer-motion";
import {
  PackageOpen,
  Star,
  MoreHorizontal,
} from "lucide-react";

// Status styling configuration
const STATUS_CONFIG = {
  active: { label: "Running", color: "bg-emerald-50 text-emerald-700 border border-emerald-200", icon: "▶" },
  paused: { label: "Idle", color: "bg-amber-50 text-amber-700 border border-amber-200", icon: "⏸" },
  error: { label: "Failed", color: "bg-rose-50 text-rose-700 border border-rose-200", icon: "⚠" },
  completed: { label: "Completed", color: "bg-blue-50 text-blue-700 border border-blue-200", icon: "✔" },
};

// Color generator for unique accents
function generateColorFromString(str) {
  const colors = [
    "#3b82f6", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981",
    "#06b6d4", "#6366f1", "#f97316", "#14b8a6", "#a855f7",
    "#ef4444", "#84cc16",
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

function formatDate(d) {
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return d;
  }
}

export default function WorkflowCard({
  workflow,
  maxSearchPerWorkflow,
  isSelected,
  onSelect,
  onPin,
  onMenu,
}) {
  const mockWorkflow = {
    threadName: "Customer Data Processing",
    status: "active",
    progress: 68,
    priority: "high",
    searches: [1, 2, 3, 4, 5],
    assignee: "Admin User",
    lastUsed: new Date().toISOString(),
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    estimatedCompletion: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
    pinned: false,
    ...workflow,
  };

  const status = mockWorkflow.status || "active";
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.paused;
  const borderColor = useMemo(() => {
    const uniqueKey = mockWorkflow.threadName + (mockWorkflow.id || "");
    return generateColorFromString(uniqueKey);
  }, [mockWorkflow.threadName, mockWorkflow.id]);

  const progress = mockWorkflow.progress || 0;
  const isPinned = mockWorkflow.pinned || false;

  return (
    <motion.article
      whileHover={{ y: -2, scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect(mockWorkflow)}
      style={{
        borderTop: `3px solid ${borderColor}`,
      }}
      className={`relative rounded-2xl bg-white shadow-[0_4px_20px_rgba(0,0,0,0.04)] 
        hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 
        overflow-hidden cursor-pointer`}
      aria-label={`Workflow card for ${mockWorkflow.threadName}`}
    >
      {/* Subtle gradient accent */}
      <div
        className="absolute inset-0 opacity-[0.04] bg-gradient-to-br from-gray-100 to-transparent pointer-events-none"
      ></div>

      <div className="relative p-5 flex flex-col gap-5">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h4 className="text-lg font-semibold text-gray-900 truncate tracking-tight">
              {mockWorkflow.threadName}
            </h4>
            <p className="text-xs text-gray-500 mt-1">
              Created {formatDate(mockWorkflow.createdAt)}
            </p>
          </div>

          {/* Status */}
          <span
            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium ${cfg.color}`}
          >
            <span>{cfg.icon}</span> {cfg.label}
          </span>
        </div>

        {/* Metrics */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-3xl font-bold text-gray-900 leading-tight">
              {(mockWorkflow.searches?.length || 0).toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 tracking-wide mt-1">Searches</p>
          </div>

          {/* Progress Circle */}
          <div className="relative w-12 h-12">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
              <path
                d="M18 2a16 16 0 1 1 0 32a16 16 0 1 1 0-32"
                fill="none"
                stroke="#f3f4f6"
                strokeWidth="3"
              />
              <path
                d="M18 2a16 16 0 1 1 0 32a16 16 0 1 1 0-32"
                fill="none"
                stroke={borderColor}
                strokeWidth="3"
                strokeDasharray={`${( mockWorkflow.searches?.length / maxSearchPerWorkflow ) * 100}, 100`}
                strokeLinecap="round"
                className="transition-all duration-700 ease-in-out"
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-gray-700">
              {Math.round((mockWorkflow.searches?.length / maxSearchPerWorkflow) * 100)}%
            </span>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between border-t border-gray-100 pt-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSelect(mockWorkflow);
            }}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg
              bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-sm
              hover:shadow-md hover:from-blue-600 hover:to-indigo-600 transition-all duration-300"
          >
            <PackageOpen size={18} /> Open
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMenu(mockWorkflow);
              }}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
              aria-label="More options"
            >
              <MoreHorizontal size={18} />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                onPin(mockWorkflow);
              }}
              className={`p-2 rounded-lg transition-all ${
                isPinned
                  ? "bg-amber-100 text-amber-600 hover:bg-amber-200"
                  : "hover:bg-gray-100 text-gray-400 hover:text-gray-700"
              }`}
              aria-label={isPinned ? "Unpin workflow" : "Pin workflow"}
            >
              <Star size={18} fill={isPinned ? "#fbbf24" : "none"} />
            </button>
          </div>
        </div>
      </div>
    </motion.article>
  );
}
